import "./MainSideMenu.scss";
import {connect} from "react-redux";
import {authTokenPropProvider} from "../../../common/tokenProvider";
import {useQuery} from '@tanstack/react-query';
import {PrototypesDispatchApi} from "../../../sections/prototypes/PrototypesSection";
import {LinearProgress, Stack} from "@mui/material";
import {useIsSuper} from "../../../state/UserContextProvider";
import {usePgexEnabled} from "../../../state/usePgexEnabled";
import {Fragment} from "react";
import {NavLink} from "react-router-dom";


type MenuItemProps = {
    iconClass: string;
    label: string;
    to: any;
    onlySuper?: boolean;
}

function MenuItem({to, iconClass, label, onlySuper}: MenuItemProps) {

    const isSuper = useIsSuper();
    if (onlySuper && !isSuper) return null;

    const baseClass = `menu-item-link`;
    return <li>
        <NavLink className={baseClass} activeClassName="is-active" to={to} exact>
            <div className="icon-text">
            <span className="icon">
                <i className={`fas fa-${iconClass}`} aria-hidden="true"/>
            </span>
                <span className="menu-item-label">{label}</span>
            </div>
        </NavLink>
    </li>;
}


interface IMenuBlock {
    label: string;
    items: MenuItemProps[];
}

function MainSideMenuInternal({token}: { token: string }) {

    const isSuper = useIsSuper();
    const pgexEnabled = usePgexEnabled();

    const menu: IMenuBlock[] = [
        {
            label: "General",
            items: [
                {to: "/", label: "Dashboard", iconClass: "tachometer-alt"},
                {to: "/playgrounds", label: "Playgrounds", iconClass: "code-branch"},
                {to: "/modules", label: "Modules", iconClass: "cube", onlySuper: true},
                ...(pgexEnabled ? [{to: "/numbers", label: "Numbers", iconClass: "book"}] : []),
            ],
        },
        ...(pgexEnabled ? [{
            label: "Builders",
            items: [
                {to: "/templates/voice", label: "Voice", iconClass: "phone-square-alt"},
                {to: "/templates/sms", label: "SMS", iconClass: "sms"},
                {to: "/templates/email", label: "Email", iconClass: "envelope"},
            ]
        }] : []),
        {
            label: "Settings",
            items: [
                {to: "/account/contexts", label: "Variable Contexts", iconClass: "cubes"},
                {to: "/account", label: "My Account", iconClass: "user"},
            ],
        }
    ];

    const {isFetching, data: prototypes} = useQuery(["prototypes"], () => {
        if (token) {
            const api = new PrototypesDispatchApi(token);
            return api.list();
        }
        return null;
    }, {
        enabled: pgexEnabled,
        keepPreviousData: true,
        refetchOnWindowFocus: false,
    });

    const groupElements = [];
    for (let group of menu) {
        const items = [];

        for (let item of group.items) {
            const key = `menu-item-${group.label}-${item.to}`;
            items.push(<MenuItem key={key} {...item}/>);
        }

        groupElements.push(
            <Fragment key={group.label}>
                <h2 className="menu-label">{group.label}</h2>
                <ul className="menu-list">{items}</ul>
            </Fragment>
        );
    }

    if (pgexEnabled && prototypes && prototypes.length > 0) {
        groupElements.push(
            <Fragment key="prototypes">
                <h2 className="menu-label" key="prototypes">Prototypes</h2>
                <ul className="menu-list" key="prototype-list">
                    {prototypes.map(prototype => <MenuItem
                        key={prototype.id}
                        to={`/prototypes/${prototype.id}`}
                        label={prototype.name}
                        iconClass="puzzle-piece"
                    />)}
                </ul>
            </Fragment>
        );
    } else if (isFetching) {
        groupElements.push(<div className="menu-item-loading" key="load-stack">
            <Stack sx={{width: "100%", color: "grey.200"}} spacing={2}>
                <LinearProgress color="inherit"/>
            </Stack>
        </div>);
    }

    if (isSuper) {
        groupElements.push(
            <Fragment key="admin">
                <div className="flush"></div>
                <h2 className="menu-label admin">Admin</h2>
                <ul className="menu-list">
                    <MenuItem to="/admin/namespaces" label="Namespaces" iconClass="layer-group"/>
                    <MenuItem to="/admin/accounts" label="Accounts" iconClass="building"/>
                    <MenuItem to="/admin/users" label="Users" iconClass="users"/>
                </ul>
            </Fragment>
        );
    }

    return (
        <aside className="main-side-menu">
            {groupElements}
        </aside>
    );
}

export const MainSideMenu = connect(authTokenPropProvider)(MainSideMenuInternal);
export default MainSideMenu as any;
